import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Radio from "@mui/material/Radio";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";

import { ResultsTableRow } from "./LoadConfigurationFormContent";

export interface ResultsTableDialogProps {
  data: ResultsTableRow[] | null;
  selectedRow: Partial<ResultsTableRow> | undefined;
  setSelectedRow: (row: Partial<ResultsTableRow> | undefined) => void;
}

export function ResultsTableDialog({
  data,
  selectedRow,
  setSelectedRow,
}: ResultsTableDialogProps): ReactNode {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const handleChangePage = (_: unknown, newPage: number): void => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = data
    ? data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  const headers = [
    {
      key: "materialnumber",
      label: t("load_configuration_dialog.material_number"),
    },
    { key: "order_code", label: t("load_configuration_dialog.order_code") },
    { key: "version", label: t("load_configuration_dialog.version") },
    { key: "status", label: t("load_configuration_dialog.status") },
    { key: "creator", label: t("load_configuration_dialog.creator") },
    { key: "update_user", label: t("load_configuration_dialog.update_user") },
    { key: "createdate", label: t("load_configuration_dialog.createdDate") },
  ];

  return (
    <>
      <Box sx={{ maxHeight: 500, overflowY: "auto" }}>
        {data && data.length > 0 ? (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell />
                {headers.map((header) => (
                  <TableCell key={header.key} sx={{ fontWeight: 500 }}>
                    {header.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => {
                    setSelectedRow(row);
                  }}
                  selected={selectedRow?.id === row.id}
                  sx={{
                    cursor: "pointer",
                    backgroundColor:
                      selectedRow?.id === row.id
                        ? "rgba(76, 175, 80, 0.15)"
                        : undefined,
                  }}
                >
                  <TableCell
                    padding="checkbox"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Radio
                      checked={selectedRow?.id === row.id}
                      onChange={() => setSelectedRow(row)}
                      value={row.id}
                      inputProps={{ "aria-label": `select row ${row.id}` }}
                    />
                  </TableCell>
                  {headers.map((header) => (
                    <TableCell key={header.key}>
                      {row[header.key as keyof ResultsTableRow]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Box>{t("no_results_found")}</Box>
        )}
      </Box>
      <TablePagination
        component="div"
        count={data ? data.length : 0}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 20]}
        labelRowsPerPage={t("load_configuration_dialog.rows_per_page")}
        sx={{
          "& .MuiInputBase-root": {
            minWidth: "min-content",
            width: "min-content",
          },
        }}
      />
    </>
  );
}
